import { z } from "zod";
import { chatChoiceShape, uuidSchema } from "./domain";

/**
 * Photos, videos, documents and voice notes sent from the chat (T-PL-37, ADR 0086).
 * The browser uploads one file per message; the worker converts what WhatsApp
 * would not take (a voice note to Ogg Opus, a `.mov` to MP4), uploads it to Meta
 * and sends it. Only inside the 24-hour window, as free text.
 */

export const OUTBOUND_MEDIA_KINDS = ["image", "video", "audio", "document"] as const;
export type OutboundMediaKind = (typeof OUTBOUND_MEDIA_KINDS)[number];

const MB = 1024 * 1024;

/** What Meta takes for each kind, after the worker's conversion. */
export const WHATSAPP_MEDIA_MAX_BYTES: Record<OutboundMediaKind, number> = {
  image: 5 * MB,
  video: 16 * MB,
  audio: 16 * MB,
  document: 100 * MB,
};

/**
 * What one upload may weigh: the proxy in front of the API takes 25 MB per
 * request. A video above Meta's 16 MB is still taken, to be compressed.
 */
export const OUTBOUND_MEDIA_UPLOAD_MAX_BYTES = 24 * MB;

/** Formats accepted from the browser, with the kind of message each one becomes. */
export const OUTBOUND_MEDIA_TYPES: Record<string, OutboundMediaKind> = {
  "image/jpeg": "image",
  "image/png": "image",
  "video/mp4": "video",
  "video/quicktime": "video",
  "video/3gpp": "video",
  "audio/ogg": "audio",
  "audio/webm": "audio",
  "audio/mp4": "audio",
  "audio/x-m4a": "audio",
  "audio/aac": "audio",
  "audio/mpeg": "audio",
  "application/pdf": "document",
  "application/msword": "document",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "document",
  "application/vnd.ms-excel": "document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "document",
  "application/vnd.ms-powerpoint": "document",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "document",
  "text/plain": "document",
};

/** The type of a file the browser did not name (some documents), from its extension. */
const EXTENSION_TYPES: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  mp4: "video/mp4",
  mov: "video/quicktime",
  "3gp": "video/3gpp",
  ogg: "audio/ogg",
  opus: "audio/ogg",
  m4a: "audio/mp4",
  aac: "audio/aac",
  mp3: "audio/mpeg",
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  txt: "text/plain",
};

/** `audio/webm;codecs=opus` → `audio/webm`; a name's extension when the type is missing. */
export function outboundMediaType(type: string, fileName = ""): string | null {
  const base = type.split(";")[0]?.trim().toLowerCase() ?? "";
  if (base && OUTBOUND_MEDIA_TYPES[base]) return base;
  if (base && base !== "application/octet-stream") return null;
  const extension = fileName.toLowerCase().split(".").pop() ?? "";
  return EXTENSION_TYPES[extension] ?? null;
}

export function outboundMediaKind(type: string, fileName = ""): OutboundMediaKind | null {
  const mimeType = outboundMediaType(type, fileName);
  return mimeType ? (OUTBOUND_MEDIA_TYPES[mimeType] ?? null) : null;
}

/** The largest upload of a type: images must already fit Meta, the rest is converted. */
export function outboundUploadMaxBytes(kind: OutboundMediaKind): number {
  return Math.min(
    OUTBOUND_MEDIA_UPLOAD_MAX_BYTES,
    kind === "video" ? OUTBOUND_MEDIA_UPLOAD_MAX_BYTES : WHATSAPP_MEDIA_MAX_BYTES[kind],
  );
}

/** A voice note longer than this stops recording, as WhatsApp's long notes. */
export const VOICE_NOTE_MAX_MS = 15 * 60_000;

/** The JSON field `meta` beside the file of `POST …/leads/:leadId/messages/media`. */
export const mediaMessageMetaSchema = z
  .object({
    /** Under a photo, a video or a document; a voice note has none. */
    caption: z.string().trim().max(1_024).default(""),
    /** Recorded in the chat: sent as a voice note (Ogg Opus, `voice: true`). */
    voice: z.boolean().default(false),
    replyToMessageId: uuidSchema.nullable().optional(),
    ...chatChoiceShape,
    idempotencyKey: z.string().trim().min(8).max(200),
  })
  .strict();
export type MediaMessageMeta = z.infer<typeof mediaMessageMetaSchema>;
